import Head from 'expo-router/head';

import { listSeedScript } from '@/components/detail/list-seed';
import { isEventPast } from '@todo/core/date-range';
import { EVENT_SHOW_BUCKET } from '@todo/core/detail/event-detail';
import { EVENT_REDIRECT_BUCKET } from '@/lib/detail/event-detail';
import type { EventShow, ShowDate } from '@todo/core/event-show';
import { cities } from '@/lib/events-data';
import {
  localizedDescription,
  localizedSeoTitle,
  localizedSummary,
  localizedTitle,
} from '@todo/core/i18n/event-content';
import { MAX_JSON_LD_DATES, eventJsonLd } from '@todo/core/seo/event-jsonld';
import { formatDateCompact, formatDateLong } from '@todo/core/i18n/format';
import { usableImageUrl } from '@todo/core/image-url';
import type { Locale } from '@todo/core/i18n/locales';
import {
  SITE_NAME,
  absoluteUrl,
  breadcrumbJsonLd,
  hreflangLinks,
  itemListJsonLd,
  ogLocale,
  seoTitle,
  truncate,
} from '@todo/core/seo';
import { prefetchElementId, serializePrefetch } from '@todo/core/static-prefetch';
import type { DbEvent } from '@todo/core/supabase';

// Per-show <Head> (title, meta, OG/Twitter, JSON-LD, baked prefetch record),
// shared by the native and web variants of app/[lang]/events/[id] so the SEO
// markup has a single source of truth. `key` forces a fresh mount on
// locale/record change — see the comment in app/[lang]/_layout.tsx for why.
//
// `related` / `relatedSeedKey` bake the "more upcoming events" block's data into
// the head so it's crawlable and hydrates cleanly: `listSeedScript` re-emits the
// build-time list seed the block reuses (city-events / events-index), and the
// ItemList JSON-LD describes the linked events. Both live in <head>, which the
// sitemap's content hash deliberately ignores, so neither churns `lastmod`.
export function EventHead({
  show,
  locale,
  related,
  relatedSeedKey,
}: {
  show: EventShow;
  locale: Locale;
  related?: DbEvent[];
  relatedSeedKey?: string;
}) {
  const { event, dates } = show;
  const cityName = event.city_id
    ? (cities.find((c) => c.id === event.city_id)?.name ?? event.city_id)
    : null;
  const routePath = `events/${event.show_slug}`;
  // Never declare a placeholder that 404s as the share image (usableImageUrl).
  const imageUrl = usableImageUrl(event.image_url);
  const canonical = absoluteUrl(locale, routePath);
  const title = localizedTitle(event, locale);
  // Prefer the purpose-built ≤160-char summary; else truncate the (localized)
  // body; else a synthesized fallback. `truncate` also cleans up whitespace.
  const metaDescription =
    localizedSummary(event, locale) ??
    truncate(
      localizedDescription(event, locale) ??
        `${title}${cityName ? ` in ${cityName}` : ''} — ${formatDateLong(event.start_ts, event.tz, locale)}. Find details and tickets on ${SITE_NAME}.`,
    );

  // A one-off show's title carries its date (it's what people search with);
  // a recurring one's can't name a single date. The name part is the generated
  // SERP-sized seoTitle (falling back to the full title); the <h1> keeps the
  // plain name.
  const titleDate =
    dates.length > 1 ? undefined : formatDateCompact(event.start_ts, event.tz, locale);
  // An ended show (no upcoming dates) still describes its last performance.
  const jsonLdDates: ShowDate[] = dates.length > 0 ? dates.slice(0, MAX_JSON_LD_DATES) : [event];

  return (
    <Head key={`${locale}:${event.show_slug}`}>
      <title>{seoTitle(localizedSeoTitle(event, locale), titleDate)}</title>
      <meta name="description" content={metaDescription} />
      {/* Ended events stay live for a 30-day SEO-retention window (see
          isEventPast / archive_past_events) but shouldn't rank as fresh
          content, so Google deindexes them while still crawling their
          internal links to upcoming events. */}
      {isEventPast(event) ? <meta name="robots" content="noindex,follow" /> : null}
      <link rel="canonical" href={canonical} />
      {hreflangLinks(routePath).map((link) => (
        <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
      ))}
      <meta property="og:type" content="event" />
      <meta property="og:locale" content={ogLocale(locale)} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonical} />
      {imageUrl ? <meta property="og:image" content={imageUrl} /> : null}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metaDescription} />
      {imageUrl ? <meta name="twitter:image" content={imageUrl} /> : null}
      <script type="application/ld+json">
        {JSON.stringify(
          jsonLdDates.length === 1
            ? eventJsonLd(event, jsonLdDates[0], cityName, canonical, locale)
            : jsonLdDates.map((date) => eventJsonLd(event, date, cityName, canonical, locale)),
        )}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbJsonLd(locale, [{ name: title }]))}
      </script>
      {related && related.length > 0 ? (
        <script type="application/ld+json">
          {JSON.stringify(
            itemListJsonLd(
              related.map((e) => ({
                name: localizedTitle(e, locale),
                url: absoluteUrl(locale, `events/${e.show_slug}`),
              })),
              canonical,
            ),
          )}
        </script>
      ) : null}
      {/* Baked record so the first client render matches this static HTML and
          hydration doesn't mismatch (#418). */}
      <script id={prefetchElementId(EVENT_SHOW_BUCKET, event.show_slug)} type="application/json">
        {serializePrefetch(show)}
      </script>
      {/* Baked list seed for the "more upcoming events" block — reuses the same
          city/region seed the block reads, so its cards hydrate without a
          refetch. Emitted last so it never displaces the singular tags above. */}
      {relatedSeedKey ? listSeedScript(relatedSeedKey) : null}
    </Head>
  );
}

/**
 * <Head> for an old per-date URL (/[lang]/events/<row id>, from before shows
 * replaced per-date pages): an instant meta refresh to `path` — the show page,
 * or the city's events page once the show is gone (event-detail.ts) — which
 * Google treats as a permanent redirect, and a canonical naming it. The site
 * is static, so this stands in for a 301 (Cloudflare's _redirects caps out
 * below the number of old URLs). generate-sitemap.js leaves these pages out.
 */
export function EventRedirectHead({
  id,
  path,
  locale,
}: {
  id: string;
  /** The target under /[lang], e.g. `events/<show_slug>` or `nice/events`. */
  path: string;
  locale: Locale;
}) {
  const target = absoluteUrl(locale, path);
  return (
    <Head key={`${locale}:${id}`}>
      <title>{SITE_NAME}</title>
      {/* The literal attribute name, not React's `httpEquiv`: Helmet writes prop
          names into the static HTML verbatim (see lowercaseHreflang in
          packages/web-build/src/fix-static-head-duplicates.js), and browsers only honour
          `http-equiv`. */}
      <meta http-equiv="refresh" content={`0; url=${target}`} />
      <link rel="canonical" href={target} />
      {/* So the client's first render finds the target without a fetch. */}
      <script id={prefetchElementId(EVENT_REDIRECT_BUCKET, id)} type="application/json">
        {serializePrefetch(path)}
      </script>
    </Head>
  );
}
